import { ArdoqId, GroupType } from '@ardoq/api-types';
import {
  isPerspectiveGroupingRuleWithWorkspace,
  PerspectiveTab,
} from '@ardoq/perspectives-sidebar';
import {
  PerspectiveGroupingRule,
  PerspectivesGroupsWorkspace,
} from '@ardoq/perspectives';
import { StatusType } from '@ardoq/status-ui';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { DatasetRelatedData, PerspectivesEditorState } from './types';
import type { ComponentTypeInfo, ReferenceTypeDetails } from '@ardoq/graph';
import type {
  GetFieldsByComponentType,
  GetFieldsByReferenceType,
} from '@ardoq/field-interface';
import { type I18nLocale, t } from '@ardoq/i18n';

export const getAvailableGroupOptionsWorkspaces = (
  groupOptionsWorkspaces: PerspectivesGroupsWorkspace[],
  workspaceFilterIds: ArdoqId[]
): PerspectivesGroupsWorkspace[] =>
  workspaceFilterIds.length > 0
    ? groupOptionsWorkspaces.filter(({ id }) => workspaceFilterIds.includes(id))
    : groupOptionsWorkspaces;

const getGroupTabPopoverContent = (
  rulesCount: number,
  i18nLocale: I18nLocale
) =>
  rulesCount === 1
    ? t(
        i18nLocale,
        'perspectiveSidebar:groupsTab.singleGroupNotApplicable',
        `This group can't be applied to the opened datasets.`
      )
    : t(
        i18nLocale,
        'perspectiveSidebar:groupsTab.multipleGroupsNotApplicable',
        `Some groups can't be applied to the opened datasets.`
      );

type TabsData = {
  groupsTab: Pick<
    PerspectivesEditorState['groupsTab'],
    'groupingRules' | 'rulesIdsWithWarnings'
  >;
};
export const getTabStatusProps = (
  tabType: PerspectiveTab,
  state: TabsData,
  i18nLocale: I18nLocale
) => {
  if (
    tabType !== PerspectiveTab.GROUPS ||
    state.groupsTab.rulesIdsWithWarnings.length === 0
  ) {
    return null;
  }

  const { groupingRules, rulesIdsWithWarnings } = state.groupsTab;
  const visibleRulesWithWarningCount = groupingRules.filter(rule =>
    rulesIdsWithWarnings.includes(rule.id)
  ).length;

  if (!visibleRulesWithWarningCount) {
    return null;
  }

  return {
    tagStatusType: StatusType.WARNING,
    tagPopoverContent: getGroupTabPopoverContent(
      groupingRules.length,
      i18nLocale
    ),
  };
};

export const getGroupingRuleIdsNotRelevantToDataset = (
  rules: PerspectiveGroupingRule[],
  datasetRelatedData?: DatasetRelatedData
) => {
  if (!datasetRelatedData) {
    return [];
  }

  return rules
    .filter(rule => {
      if (!isPerspectiveGroupingRuleWithWorkspace(rule)) {
        return false;
      }

      const options =
        datasetRelatedData.groupingOptionsByWorkspaceId[rule.workspaceId];
      if (!options) {
        return true;
      }

      switch (rule.type) {
        case GroupType.PARENT:
          return !options.componentTypeIds.has(rule.targetId);
        case GroupType.FIELD:
          return !options.componentFieldIds.has(rule.targetId);
        case GroupType.REFERENCE: {
          return !options.referenceTypeIds.has(rule.targetId);
        }
        case GroupType.TAG: {
          const targetId = rule.targetId;
          return !targetId || !options.tagIds.has(targetId);
        }
        default:
          exhaustiveCheck(rule);
      }

      return true;
    })
    .map(rule => rule.id);
};

type NamedTypeEntry<TId> = {
  name: string;
  id: TId;
  workspaceModelId: string;
};

// Builds a map from type name to merged, deduplicated field names.
// When multiple types share the same name (across workspaces/datasets),
// their fields are accumulated rather than overwritten.
const buildNameAndFieldsIndex = <TId>(
  items: NamedTypeEntry<TId>[],
  getFieldsById: (id: TId, modelId: string) => { name: string }[],
  datasetFieldNames?: Set<string>
): Map<string, string[]> =>
  items.reduce((acc, { name, id, workspaceModelId }) => {
    const fields = getFieldsById(id, workspaceModelId)
      .map(field => field.name)
      .filter(fieldName =>
        datasetFieldNames ? datasetFieldNames.has(fieldName) : true
      );
    const existing = acc.get(name) ?? [];
    acc.set(name, Array.from(new Set([...existing, ...fields])));
    return acc;
  }, new Map<string, string[]>());

export const buildReferenceNameAndFieldsIndex = (
  referenceTypes: ReferenceTypeDetails[],
  getFieldsByReferenceType: GetFieldsByReferenceType,
  datasetReferenceFieldNames?: Set<string>
): Map<string, string[]> =>
  buildNameAndFieldsIndex(
    referenceTypes.map(
      ({ referenceType: { name }, typeId, workspaceModelId }) => ({
        name,
        id: typeId,
        workspaceModelId,
      })
    ),
    getFieldsByReferenceType,
    datasetReferenceFieldNames
  );

export const buildComponentNameAndFieldsIndex = (
  componentTypes: ComponentTypeInfo[],
  getFieldsByComponentType: GetFieldsByComponentType,
  datasetComponentFieldNames?: Set<string>
): Map<string, string[]> =>
  buildNameAndFieldsIndex(
    componentTypes.map(({ componentType: { id, name }, workspaceModelId }) => ({
      name,
      id,
      workspaceModelId,
    })),
    getFieldsByComponentType,
    datasetComponentFieldNames
  );
